import { useState } from "react";
import { Prohibit, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money } from "@/lib/money";

// Void an invoice (kept on record, balance zeroed). Owner chooses whether linked
// timesheets go back to "approved" (re-invoiceable) or stay attached (never billed).
export default function VoidInvoiceModal({ invoice: inv, onClose, onVoided }) {
  const [reason, setReason] = useState("");
  const [release, setRelease] = useState(true);
  const [busy, setBusy] = useState(false);
  const tsCount = (inv.timesheet_ids || []).length;
  const paid = Number(inv.total || 0) - Number(inv.balance_due || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) { toast.error("Please provide a reason (min 3 characters)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/void`, { reason: reason.trim(), release_timesheets: release });
      const tail = data.released_timesheets ? ` · ${data.released_timesheets} timesheet(s) released for re-invoicing`
        : data.retained_timesheets ? ` · ${data.retained_timesheets} timesheet(s) kept on it (won't be billed again)` : "";
      toast.success(`Invoice ${inv.number} voided${tail}.`);
      onVoided?.(data);
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="void-invoice-modal" onClick={() => !busy && onClose()}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Prohibit size={22} className="text-slate-700" weight="duotone" />
            <h2 className="text-xl font-bold">Void invoice {inv.number}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>
        <div className="text-sm text-slate-600 space-y-1">
          <p>Voiding keeps the invoice in your records (visible under <b>Voided</b> and <b>All</b>) but:</p>
          <ul className="list-disc pl-5 text-slate-500">
            <li>Status becomes <b>VOIDED</b> and the {money(inv.balance_due)} balance is zeroed — it drops off statements, aging and totals</li>
            <li>The invoice number {inv.number} stays used (no gaps)</li>
            {paid > 0 && <li className="text-amber-700">{money(paid)} already recorded as paid stays on the record</li>}
            {["sent", "partially_paid", "overdue"].includes(inv.status) && <li>The client is <b>not</b> notified — tell them separately if needed</li>}
          </ul>
        </div>

        {tsCount > 0 && (
          <div className="mt-4 border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="void-ts-choice">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{tsCount} linked timesheet{tsCount === 1 ? "" : "s"} — what should happen to {tsCount === 1 ? "it" : "them"}?</div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" name="void-ts" checked={release} onChange={() => setRelease(true)} data-testid="void-release-yes" className="mt-1 accent-emerald-600" />
              <span><span className="font-semibold text-slate-800">Release for re-invoicing</span>
                <span className="block text-xs text-slate-500">Back to <b>approved</b> — shows as ready to invoice again (e.g. wrong client, wrong amount, duplicate).</span></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer mt-2">
              <input type="radio" name="void-ts" checked={!release} onChange={() => setRelease(false)} data-testid="void-release-no" className="mt-1 accent-emerald-600" />
              <span><span className="font-semibold text-slate-800">Keep on this voided invoice — never bill</span>
                <span className="block text-xs text-slate-500">Stays <b>invoiced</b> so it won't be picked up again (e.g. job problem, amount forgiven). Workers still get paid through the pay run. You can release later from the invoice page.</span></span>
            </label>
          </div>
        )}

        <label className="block mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Reason (required)</label>
        <textarea autoFocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="void-reason-input"
          placeholder="e.g. Duplicate invoice; Amount forgiven — job dispute; Wrong client"
          className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500" />
        <p className="text-xs text-slate-400 mt-2">Recorded in the audit trail. Voiding can't be undone — use Delete instead if you want the invoice gone entirely.</p>
        <div className="mt-5 flex gap-3 justify-end">
          <button type="button" onClick={onClose} disabled={busy} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button data-testid="confirm-void-btn" disabled={busy || reason.trim().length < 3}
            className="h-11 px-4 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-md disabled:opacity-60">
            {busy ? "Voiding…" : "Void invoice"}
          </button>
        </div>
      </form>
    </div>
  );
}
